import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ColumnHeader, { SortType } from '../../../ColumnHeader';
import DebouncedFilterInput from '../../../FormElementsStandalone/DebouncedFilterInput';
import Pagination from '../../../Pagination';
import PopOverMenu from '../../../PopOverMenu';
import useIsMobile from '../../../hooks/useIsMobile';
import { computeSkeletonRowCount } from '../../../SavedLists/skeleton/computeSkeletonRowCount';
import { pluralize } from '../../../../utils/formatting';
import DeleteListConfirmModal from '../DeleteListConfirmModal';
import HubError from '../HubError';
import SortListsModal from '../SortListsModal';
import { sortLabelFor } from '../SortListsModal/sortListsOptions';
import { SavedListSortColumn, SAVED_LISTS_PAGE_SIZE } from '../constants';
import HubLoading from './HubLoading';

import styles from './myListsHub.scss';

const DESKTOP_SEARCH_PLACEHOLDER = 'Search Lists';
const MOBILE_SEARCH_PLACEHOLDER = 'Find a List';
const LEGEND_LABEL = 'Recent Activity';
const DELETE_MENU_VALUE = 'delete';
// Up/down arrows sort icon shared with the List Detail sort control (Figma, 16×16).
const SORT_ICON = '/img/v3/icons/icon-sort-arrows.svg';

const MyListsHub = ({
    'data-testid': dataTestId,
    lists,
    total,
    totalPages,
    page,
    sortColumn,
    sortType,
    search,
    isLoading,
    isError,
    onSearch,
    onSort,
    onPageChange,
    onDeleteList,
    onOpenList,
    onRetry,
}) => {
    const { isMobileWidth } = useIsMobile();
    const [listPendingDelete, setListPendingDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);

    const closeDeleteModal = () => {
        setListPendingDelete(null);
        setIsDeleting(false);
    };

    const handleConfirmDelete = () => {
        setIsDeleting(true);
        onDeleteList(listPendingDelete.id)
            .then(closeDeleteModal)
            .catch(() => setIsDeleting(false));
    };

    const handleSortApply = (column, type) => {
        onSort(column, type);
        setIsSortModalOpen(false);
    };

    const currentSortLabel = sortLabelFor(sortColumn, sortType);

    // On the first load the hub's total isn't known until the lists-search resolves (there's no separate
    // count call), so `lists` is still empty and the skeleton sizes to the fallback. On a refetch
    // (sort / search / pagination) the prior rows are still mounted, so `total` is the known prior count
    // and the skeleton sizes to the incoming page — real rows then drop in with no layout shift.
    const skeletonTotal = lists.length === 0 ? undefined : total;
    const skeletonRowCount = computeSkeletonRowCount(skeletonTotal, page, SAVED_LISTS_PAGE_SIZE);

    // On the first load the count isn't known yet (total is still 0), so suppress it rather than flash
    // "Showing 0 Lists" over the skeleton. A refetch keeps the prior count (total > 0), so it stays put.
    const renderCount = () => {
        if (isLoading && total === 0) {
            return null;
        }
        return (
            <span className={styles.count} data-testid={dataTestId && `${dataTestId}.count`}>
                {`Showing ${total} ${pluralize('List', total)}`}
            </span>
        );
    };

    const renderLegend = () => (
        <span className={styles.legend} data-testid={dataTestId && `${dataTestId}.legend`}>
            <span className={styles.legendDot} />
            <span className={styles.legendLabel} data-testid={dataTestId && `${dataTestId}.legend.label`}>
                {LEGEND_LABEL}
            </span>
        </span>
    );

    const renderSearch = () => (
        <div className={styles.search}>
            <DebouncedFilterInput
                value={search}
                onChange={onSearch}
                placeholder={isMobileWidth ? MOBILE_SEARCH_PLACEHOLDER : DESKTOP_SEARCH_PLACEHOLDER}
                className={styles.searchInput}
                data-testid={dataTestId && `${dataTestId}.search`}
            />
        </div>
    );

    const renderSortTrigger = () => (
        <button
            type="button"
            className={styles.sortAnchor}
            onClick={() => setIsSortModalOpen(true)}
            data-testid={dataTestId && `${dataTestId}.sort-trigger`}
        >
            <img className={styles.sortIcon} src={SORT_ICON} alt="" data-testid={dataTestId && `${dataTestId}.sort-trigger.icon`} />
            {`Sort by: ${currentSortLabel}`}
        </button>
    );

    const renderColumnHeaders = () => (
        <div className={styles.tableHeader} data-testid={dataTestId && `${dataTestId}.table-header`}>
            <ColumnHeader
                className={styles.colName}
                isSortable
                columnName="Name"
                isSelected={sortColumn === SavedListSortColumn.NAME}
                sortType={sortType}
                initialSort={SortType.ASC}
                onClick={type => onSort(SavedListSortColumn.NAME, type)}
                data-testid={dataTestId && `${dataTestId}.col-name`}
            />
            <ColumnHeader
                className={styles.colPlayers}
                isSortable
                columnName="# Players"
                isSelected={sortColumn === SavedListSortColumn.PLAYERS}
                sortType={sortType}
                initialSort={SortType.DESC}
                onClick={type => onSort(SavedListSortColumn.PLAYERS, type)}
                data-testid={dataTestId && `${dataTestId}.col-players`}
            />
            <ColumnHeader
                className={styles.colActivity}
                isSortable
                columnName="Recent Activity"
                isSelected={sortColumn === SavedListSortColumn.ACTIVITY}
                sortType={sortType}
                initialSort={SortType.DESC}
                onClick={type => onSort(SavedListSortColumn.ACTIVITY, type)}
                data-testid={dataTestId && `${dataTestId}.col-activity`}
            />
        </div>
    );

    const renderRow = (list) => {
        const rowTestId = dataTestId && `${dataTestId}.row-${list.id}`;
        const deleteMenuItems = [{ value: DELETE_MENU_VALUE, label: 'Delete List' }];

        return (
            <div
                key={list.id}
                className={`${styles.row} ${isMobileWidth ? styles.rowMobile : ''}`}
                data-testid={rowTestId}
            >
                <button
                    type="button"
                    className={styles.rowName}
                    onClick={() => onOpenList(list.id)}
                    data-testid={rowTestId && `${rowTestId}.name`}
                >
                    {list.name}
                </button>
                <span className={styles.rowPlayers} data-testid={rowTestId && `${rowTestId}.players`}>
                    {list.playerCount}
                    <span className={styles.rowPlayersLabel} data-testid={rowTestId && `${rowTestId}.players.label`}>
                        {' Players'}
                    </span>
                </span>
                <span className={styles.rowActivity} data-testid={rowTestId && `${rowTestId}.activity`}>
                    {list.recentActivity && (
                        <span className={styles.activityDot} data-testid={rowTestId && `${rowTestId}.activity-dot`} />
                    )}
                </span>
                {!list.isDefault && (isMobileWidth ? (
                    // Mobile: the row's only action is Delete, and an absolutely-positioned dropdown gets
                    // clipped by the card — so tapping the kebab opens the delete confirmation directly.
                    <button
                        type="button"
                        className={styles.rowMenuButton}
                        onClick={() => setListPendingDelete(list)}
                        data-testid={rowTestId && `${rowTestId}.menu`}
                    >
                        <span className="far fa-ellipsis-v" />
                    </button>
                ) : (
                    <PopOverMenu
                        menuItems={deleteMenuItems}
                        onSelected={item => item.value === DELETE_MENU_VALUE && setListPendingDelete(list)}
                        anchorClass={styles.menuAnchor}
                        popOverMenuClass={styles.rowMenu}
                        data-testid={rowTestId && `${rowTestId}.menu`}
                    />
                ))}
            </div>
        );
    };

    const renderRowsArea = () => {
        if (isError) {
            return <HubError data-testid={dataTestId && `${dataTestId}.error`} onRetry={onRetry} />;
        }
        if (isLoading) {
            return (
                <HubLoading
                    rowCount={skeletonRowCount}
                    isMobile={isMobileWidth}
                    data-testid={dataTestId && `${dataTestId}.loading`}
                />
            );
        }
        return lists.map(renderRow);
    };

    return (
        <div className={`${styles.hub} ${isMobileWidth ? styles.hubMobile : ''}`} data-testid={dataTestId || undefined}>
            {isMobileWidth ? (
                <div className={styles.mobileControls}>
                    {renderSearch()}
                    <div className={styles.mobileSummaryRow}>
                        {renderCount()}
                        {renderSortTrigger()}
                    </div>
                    <div className={styles.mobileDivider} data-testid={dataTestId && `${dataTestId}.mobile-divider`} />
                    {renderLegend()}
                </div>
            ) : (
                <div className={styles.desktopControls}>
                    <div className={styles.summary}>
                        {renderCount()}
                        <span className={styles.summaryDivider} />
                        {renderLegend()}
                    </div>
                    {renderSearch()}
                </div>
            )}
            {!isMobileWidth && renderColumnHeaders()}
            <div className={styles.rows} data-testid={dataTestId && `${dataTestId}.rows`}>
                {renderRowsArea()}
            </div>
            {totalPages > 1 && (
                <div className={styles.paginationWrapper}>
                    <Pagination
                        currentPage={page}
                        lastPage={totalPages}
                        onClick={onPageChange}
                        data-testid={dataTestId && `${dataTestId}.pagination`}
                    />
                </div>
            )}
            {listPendingDelete && (
                <DeleteListConfirmModal
                    isDeleting={isDeleting}
                    onConfirm={handleConfirmDelete}
                    onCancel={closeDeleteModal}
                />
            )}
            {isMobileWidth && isSortModalOpen && (
                <SortListsModal
                    sortColumn={sortColumn}
                    sortType={sortType}
                    onApply={handleSortApply}
                    onCancel={() => setIsSortModalOpen(false)}
                />
            )}
        </div>
    );
};

MyListsHub.propTypes = {
    'data-testid': PropTypes.string,
    lists: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        name: PropTypes.string,
        isDefault: PropTypes.bool,
        playerCount: PropTypes.number,
        recentActivity: PropTypes.bool,
    })),
    total: PropTypes.number,
    totalPages: PropTypes.number,
    page: PropTypes.number,
    sortColumn: PropTypes.string,
    sortType: PropTypes.string,
    search: PropTypes.string,
    isLoading: PropTypes.bool,
    isError: PropTypes.bool,
    onSearch: PropTypes.func.isRequired,
    onSort: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired,
    onDeleteList: PropTypes.func.isRequired,
    onOpenList: PropTypes.func,
    onRetry: PropTypes.func,
};

MyListsHub.defaultProps = {
    'data-testid': null,
    lists: [],
    total: 0,
    totalPages: 0,
    page: 1,
    sortColumn: null,
    sortType: null,
    search: '',
    isLoading: false,
    isError: false,
    onOpenList: () => {},
    onRetry: () => {},
};

export default MyListsHub;
